import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getAllProducts } from '@/services/products.service';
import { ordenAleatorio } from '@/utils/helpers';
import type { Product } from '@/types';
import { useCart } from '@/context/CartContext';

function calcDiscount(original: number, sale: number): number {
  if (!original || original <= sale) return 0;
  return Math.round(((original - sale) / original) * 100);
}

function formatUSD(n: number): string {
  return `$${n.toLocaleString('es-CU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── OfferCard — rediseñada ───────────────────────────────────────────────────
function OfferCard({ product, index }: { product: Product; index: number }) {
  const { addItem, openCart } = useCart();
  const [cartState, setCartState] = useState<'idle' | 'adding' | 'added'>('idle');
  const [imgErr, setImgErr] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const discount = product.originalPrice
    ? calcDiscount(product.originalPrice, product.price)
    : 0;

  const savings = product.originalPrice && product.originalPrice > product.price
    ? product.originalPrice - product.price
    : 0;

  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!product.inStock || cartState !== 'idle') return;
    setCartState('adding');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      addItem(product, 1);
      openCart();
      setCartState('added');
      timerRef.current = setTimeout(() => setCartState('idle'), 2200);
    }, 380);
  };

  const fallback = 'https://readdy.ai/api/search-image?query=electrical%20product%20solar%20energy%20tool%20clean%20white%20background%20minimal%20studio%20ecommerce&width=400&height=400&seq=offer-fallback-01&orientation=squarish';

  // Accent colors cycling
  const accents = [
    { ring: '#4bbfdf', badge: 'bg-[#4bbfdf]', savings: 'text-[#1e2d4a] bg-[#e8f8fd] border-[#b3e8f5]' },
    { ring: '#ef4444', badge: 'bg-rose-500',  savings: 'text-rose-600 bg-rose-50 border-rose-100' },
    { ring: '#10b981', badge: 'bg-emerald-500', savings: 'text-emerald-700 bg-emerald-50 border-emerald-100' },
    { ring: '#1e2d4a', badge: 'bg-[#1e2d4a]', savings: 'text-[#1e2d4a] bg-[#e8f8fd] border-[#b3e8f5]' },
  ];
  const accent = accents[index % accents.length];

  return (
    <Link
      to={`/products/${product.id}`}
      className="group relative flex flex-col bg-white rounded-2xl overflow-hidden cursor-pointer shrink-0 transition-all duration-300 hover:-translate-y-1.5"
      style={{
        width: '220px',
        border: '1.5px solid #f1f5f9',
        transition: 'transform 0.25s ease, border-color 0.2s ease',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = accent.ring; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = '#f1f5f9'; }}
    >
      {/* Discount badge — grande y llamativo */}
      {discount > 0 && (
        <div className="absolute top-0 right-0 z-20">
          <div
            className={`${accent.badge} text-white font-black text-[13px] px-3 py-1.5 rounded-bl-2xl rounded-tr-2xl flex items-center gap-1`}
          >
            <i className="ri-arrow-down-line text-xs" />
            {discount}%
          </div>
        </div>
      )}

      {/* Image */}
      <div className="relative bg-neutral-50 overflow-hidden" style={{ height: '180px' }}>
        {!product.inStock && (
          <div className="absolute inset-0 z-10 bg-white/75 flex items-center justify-center">
            <span className="text-[11px] font-semibold text-neutral-500 bg-white border border-neutral-200 px-3 py-1 rounded-full">
              Sin stock
            </span>
          </div>
        )}
        <img
          src={imgErr ? fallback : product.thumbnail}
          alt={product.name}
          loading="lazy"
          onError={() => setImgErr(true)}
          decoding="async"
          className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-[1.07]"
        />
      </div>

      {/* Info */}
      <div className="flex flex-col flex-1 px-3.5 pt-3 pb-3.5">
        {/* Category */}
        <span className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 mb-1 truncate">
          {product.categoryId.replace(/-/g, ' ')}
        </span>

        {/* Name */}
        <h3 className="font-semibold text-[12.5px] leading-snug text-neutral-800 line-clamp-2 flex-1" style={{ minHeight: '2.5em' }}>
          {product.name}
        </h3>

        {/* Price block */}
        <div className="mt-2.5">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-extrabold text-[20px] leading-none text-neutral-900">
              {formatUSD(product.price)}
            </span>
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="text-[12px] text-neutral-400 line-through leading-none">
                {formatUSD(product.originalPrice)}
              </span>
            )}
          </div>

          {/* Savings pill */}
          {savings > 0 && (
            <div className="mt-1.5">
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${accent.savings}`}>
                <i className="ri-money-dollar-circle-line" />
                Ahorras {formatUSD(savings)}
              </span>
            </div>
          )}
        </div>

        {/* CTA button */}
        <button
          onClick={handleAdd}
          disabled={!product.inStock || cartState === 'adding'}
          className={`mt-3 w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[12px] font-bold transition-all duration-200 cursor-pointer whitespace-nowrap min-h-[40px] shrink-0 ${
            cartState === 'added'
              ? 'bg-emerald-500 text-white'
              : cartState === 'adding'
              ? 'bg-neutral-200 text-neutral-500'
              : product.inStock
              ? `${accent.badge} text-white active:scale-95`
              : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
          }`}
        >
          {cartState === 'added' ? (
            <><i className="ri-check-line" />Agregado</>
          ) : cartState === 'adding' ? (
            <><i className="ri-loader-4-line animate-spin" />Agregando...</>
          ) : (
            <><i className="ri-shopping-cart-line" />Agregar</>
          )}
        </button>
      </div>
    </Link>
  );
}

// ─── Mobile offer card — 2 col grid ──────────────────────────────────────────
function MobileOfferCard({ product, index }: { product: Product; index: number }) {
  const { addItem, openCart } = useCart();
  const [cartState, setCartState] = useState<'idle' | 'adding' | 'added'>('idle');
  const [imgErr, setImgErr] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const discount = product.originalPrice
    ? calcDiscount(product.originalPrice, product.price)
    : 0;

  const savings = product.originalPrice && product.originalPrice > product.price
    ? product.originalPrice - product.price
    : 0;

  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!product.inStock || cartState !== 'idle') return;
    setCartState('adding');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      addItem(product, 1);
      openCart();
      setCartState('added');
      timerRef.current = setTimeout(() => setCartState('idle'), 2200);
    }, 380);
  };

  const fallback = 'https://readdy.ai/api/search-image?query=electrical%20product%20solar%20energy%20tool%20clean%20white%20background%20minimal%20studio%20ecommerce&width=400&height=400&seq=offer-fallback-01&orientation=squarish';

  const badgeColors = ['bg-[#4bbfdf]', 'bg-rose-500', 'bg-emerald-500', 'bg-[#1e2d4a]'];
  const badge = badgeColors[index % badgeColors.length];

  return (
    <Link
      to={`/products/${product.id}`}
      className="group flex flex-col bg-white rounded-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform duration-150 border border-neutral-100 h-full"
    >
      {/* Image */}
      <div className="relative bg-neutral-50 overflow-hidden shrink-0" style={{ aspectRatio: '1/1' }}>
        {discount > 0 && (
          <div className="absolute top-0 right-0 z-10">
            <div className={`${badge} text-white font-black text-[11px] px-2 py-1 rounded-bl-xl rounded-tr-xl`}>
              -{discount}%
            </div>
          </div>
        )}
        {!product.inStock && (
          <div className="absolute inset-0 z-10 bg-white/70 flex items-center justify-center">
            <span className="text-[10px] font-semibold text-neutral-500 bg-white border border-neutral-200 px-2 py-0.5 rounded-full">
              Sin stock
            </span>
          </div>
        )}
        <img
          src={imgErr ? fallback : product.thumbnail}
          alt={product.name}
          loading="lazy"
          onError={() => setImgErr(true)}
          decoding="async"
          className="w-full h-full object-contain p-3 transition-transform duration-300 group-active:scale-105"
        />
      </div>

      {/* Info */}
      <div className="flex flex-col flex-1 p-2.5">
        <span className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 mb-1 truncate">
          {product.categoryId.replace(/-/g, ' ')}
        </span>
        <h3 className="text-[12px] font-semibold text-neutral-800 leading-snug line-clamp-2 flex-1" style={{ minHeight: '2.6em' }}>
          {product.name}
        </h3>

        <div className="mt-2">
          <div className="flex items-baseline gap-1 flex-wrap">
            <span className="text-[15px] font-extrabold text-neutral-900 leading-none">
              {formatUSD(product.price)}
            </span>
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="text-[10px] text-neutral-400 line-through">
                {formatUSD(product.originalPrice)}
              </span>
            )}
          </div>
          {savings > 0 && (
            <span className="text-[9px] font-bold text-emerald-600 block mt-0.5">
              Ahorras {formatUSD(savings)}
            </span>
          )}
        </div>

        <button
          onClick={handleAdd}
          disabled={!product.inStock || cartState === 'adding'}
          className={`mt-2 w-full flex items-center justify-center gap-1 py-2 rounded-xl text-[11px] font-bold transition-all duration-200 cursor-pointer whitespace-nowrap shrink-0 ${
            cartState === 'added' ? 'bg-emerald-500 text-white' :
            cartState === 'adding' ? 'bg-neutral-200 text-neutral-500' :
            product.inStock ? `${badge} text-white active:scale-95` :
            'bg-neutral-100 text-neutral-400 cursor-not-allowed'
          }`}
        >
          {cartState === 'added' ? <><i className="ri-check-line text-xs" />Agregado</> :
           cartState === 'adding' ? <><i className="ri-loader-4-line animate-spin text-xs" />...</> :
           <><i className="ri-shopping-cart-line text-xs" />Agregar</>}
        </button>
      </div>
    </Link>
  );
}

// ─── OffersSection ────────────────────────────────────────────────────────────
export default function OffersSection() {
  const [offers, setOffers] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getAllProducts()
      .then((all) => {
        const onSale = all.filter((p) => {
          const hasOfertaField = p.tags.some((t) =>
            t.toLowerCase().includes('oferta') || t.toLowerCase().includes('descuento'),
          );
          const hasPriceDiscount = p.originalPrice !== undefined && p.originalPrice > p.price;
          return hasOfertaField || hasPriceDiscount;
        });
        setOffers(ordenAleatorio(onSale));
      })
      .catch(() => setOffers([]))
      .finally(() => setLoading(false));
  }, []);

  const scrollLeft = () => scrollRef.current?.scrollBy({ left: -480, behavior: 'smooth' });
  const scrollRight = () => scrollRef.current?.scrollBy({ left: 480, behavior: 'smooth' });

  if (!loading && offers.length === 0) return null;

  return (
    <section className="py-0 overflow-hidden">
      {/* ── Dark header band ── */}
      <div className="bg-white text-stone-900">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {/* Flash icon */}
            <div className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0" style={{ background: '#4bbfdf' }}>
              <i className="ri-flashlight-fill text-white text-base" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-extrabold text-lg md:text-xl text-stone-900 leading-none" style={{ letterSpacing: '-0.02em' }}>
                  Ofertas especiales
                </h2>
                {!loading && offers.length > 0 && (
                  <span className="inline-flex items-center text-[10px] font-black px-2 py-0.5 rounded-full text-white uppercase tracking-wider" style={{ background: '#4bbfdf' }}>
                    {offers.length} productos
                  </span>
                )}
              </div>
              <p className="text-stone-500 text-xs mt-0.5 font-medium">Precios rebajados · Tiempo limitado</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Scroll arrows */}
            {!loading && offers.length > 3 && (
              <div className="hidden md:flex items-center gap-1.5">
                <button
                  onClick={scrollLeft}
                  className="w-8 h-8 flex items-center justify-center rounded-full border border-stone-200 text-stone-500 hover:border-[#4bbfdf] hover:text-[#4bbfdf] transition-all cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-base" />
                </button>
                <button
                  onClick={scrollRight}
                  className="w-8 h-8 flex items-center justify-center rounded-full border border-stone-200 text-stone-500 hover:border-[#4bbfdf] hover:text-[#4bbfdf] transition-all cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-base" />
                </button>
              </div>
            )}
            <Link
              to="/products"
              className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-[#4bbfdf] hover:text-[#1e2d4a] transition-colors cursor-pointer whitespace-nowrap"
            >
              Ver todos <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>

      {/* ── Cards area ── */}
      <div className="bg-white pb-6">
        <div className="max-w-7xl mx-auto px-4 md:px-6">

          {/* Loading skeleton */}
          {loading && (
            <div className="flex gap-4 overflow-hidden">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="shrink-0 w-[220px] rounded-2xl bg-neutral-100 overflow-hidden animate-pulse">
                  <div className="h-[180px] bg-neutral-200" />
                  <div className="p-4 space-y-2">
                    <div className="h-3 w-12 bg-neutral-200 rounded" />
                    <div className="h-4 w-full bg-neutral-200 rounded" />
                    <div className="h-4 w-3/4 bg-neutral-200 rounded" />
                    <div className="h-6 w-24 bg-neutral-200 rounded mt-2" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* MOBILE: 2-col grid */}
          {!loading && offers.length > 0 && (
            <div className="md:hidden grid grid-cols-2 gap-3 items-stretch">
              {offers.map((product, i) => (
                <MobileOfferCard key={product.id} product={product} index={i} />
              ))}
            </div>
          )}

          {/* DESKTOP: horizontal scroll */}
          {!loading && offers.length > 0 && (
            <div
              ref={scrollRef}
              className="hidden md:flex gap-4 overflow-x-auto pb-1"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {offers.map((product, i) => (
                <OfferCard key={product.id} product={product} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>

    </section>
  );
}
